import type { ReactiveVar } from '@apollo/client'

export type BatchedReactiveVar<T> = {
  // Read current value immediately from the underlying reactive var
  read: () => T
  // Schedule an update to the reactive var:
  // 1. create a queue of updates called in the same tick, and schedules a `flush` function to clear the queue after the tick
  // 2. `flush` then calls each `update` operation, passing in the output of the previous `update` so nothing is lost
  // 3. the final output is then written into the reactiveVar
  // This ensures all updates are applied in the correct order, without "clobbering" (overwriting) each other
  // and without causing a flash of each update's state which would happen with a regular queue, not using queueMicrotask
  update: (deriveNext: (currentValue: T) => T) => void
}

/**
 * Or in GPT language:
 * Wrap an existing ReactiveVar<T> so that multiple synchronous writes/updates are
 * batched and applied once in a microtask, preventing stale-state clobbering.
 *
 * Why batching?
 * - Apollo ReactiveVar writes are synchronous. If multiple components call setters back-to-back
 *   in the same tick, each may read a stale snapshot and overwrite each other.
 * - This wrapper collects those operations synchronously and applies them once, in order,
 *   starting from the latest value at flush time.
 *
 * Why a microtask?
 * - A microtask runs after the current call stack completes but before the next macrotask and
 *   before the browser paints. That means:
 *   - All same-tick updates merge into one coherent state (no last-write-wins clobbering).
 *   - We trigger a single ReactiveVar write → a single React re-render from useReactiveVar.
 *   - The UI reflects the final merged state without transient flicker.
 * - Using a macrotask (setTimeout 0) would defer to the next event loop turn and may introduce
 *   visible intermediate states and extra latency.
 */
export function createBatchedReactiveVar<T>(
  reactiveVar: ReactiveVar<T>
): BatchedReactiveVar<T> {
  let pendingOperations: Array<(currentValue: T) => T> = []
  let isFlushScheduled = false

  const flushPendingOperations = () => {
    try {
      // read initial value
      let nextValue = reactiveVar()

      // call the updates sequentially passing in the previous result each time
      for (const operation of pendingOperations) {
        nextValue = operation(nextValue)
      }

      // write final state
      reactiveVar(nextValue)
    } finally {
      // reset pending operations and scheduled flush
      pendingOperations = []
      isFlushScheduled = false
    }
  }

  const scheduleFlush = () => {
    // already scheduled, `flushPendingOperations` will be called at end of tick
    if (isFlushScheduled) {
      return
    }

    // not already scheduled, set to scheduled
    isFlushScheduled = true

    // Schedule the flush in a microtask so it runs after the current synchronous stack
    // but before the next macrotask and before paint
    queueMicrotask(flushPendingOperations)
  }

  return {
    read: () => reactiveVar(),
    update: (deriveNext: (currentValue: T) => T) => {
      pendingOperations.push(deriveNext)
      scheduleFlush()
    },
  }
}
